import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Loader2, Zap, Crown, Rocket, Tag } from "lucide-react";
import { toast } from "sonner";

const ICONS = [Zap, Rocket, Crown];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [packages, setPackages] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/packages"), api.get("/me/subscription")]);
    setPackages(p.data);
    setSub(s.data);
  };
  useEffect(() => { load(); }, []);

  const applyCoupon = async () => {
    if (!coupon || packages.length === 0) return;
    try {
      const { data } = await api.post("/checkout/validate-coupon", { code: coupon, package_id: packages[0].id });
      setCouponInfo(data);
      toast.success(`Coupon applied — ${data.percent_off}% off`);
    } catch (err) {
      setCouponInfo(null);
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid coupon");
    }
  };

  const startFree = async (pkg) => {
    setBusy(pkg.id);
    try {
      await api.post("/checkout/free", { package_id: pkg.id });
      toast.success("Free plan activated!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  const buy = async (pkg) => {
    setBusy(pkg.id);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); return; }
      const { data } = await api.post("/checkout/order", { package_id: pkg.id, coupon_code: couponInfo ? coupon : undefined });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: settings?.site_name || "MailShift",
        description: data.package_name,
        order_id: data.order_id,
        theme: { color: settings?.brand_color || "#2563EB" },
        handler: async (resp) => {
          try {
            await api.post("/checkout/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful — plan activated!");
            navigate("/");
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
          }
        },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed"));
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-10 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pricing</p>
        <h1 className="mt-2 font-heading text-4xl font-bold tracking-tighter sm:text-5xl">
          {settings?.pricing_title || "Simple, transparent pricing"}
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          {settings?.pricing_subtitle || "Choose a plan that fits your migration needs."}
        </p>
      </div>

      {sub?.is_admin && (
        <div className="mb-8 border border-emerald-200 bg-emerald-50 p-4 text-center text-sm text-emerald-700">
          You are an admin — unlimited access, no plan required.
        </div>
      )}
      {sub?.subscription?.is_active && (
        <div className="mb-8 border border-primary/30 bg-accent p-4 text-center text-sm" data-testid="current-plan-banner">
          Active plan: <strong>{sub.subscription.package_name}</strong> · {sub.subscription.migrations_used}/{sub.subscription.max_migrations === -1 ? "∞" : sub.subscription.max_migrations} migrations used
        </div>
      )}

      <div className="mx-auto mb-8 flex max-w-md items-center gap-2 border border-border bg-card p-2">
        <Tag className="ml-2 h-4 w-4 text-muted-foreground" />
        <Input value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponInfo(null); }}
               placeholder="Have a coupon code?" className="border-0 focus-visible:ring-0" data-testid="coupon-input" />
        <Button variant="outline" size="sm" onClick={applyCoupon} data-testid="apply-coupon-button">Apply</Button>
      </div>
      {couponInfo && (
        <p className="mb-6 text-center text-sm font-medium text-emerald-600" data-testid="coupon-applied">
          🎉 {couponInfo.percent_off}% off applied to all plans
        </p>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {packages.map((p, i) => {
          const Icon = ICONS[i % ICONS.length];
          const isCurrent = sub?.subscription?.is_active && sub.subscription.package_id === p.id;
          const hasActivePlan = !!sub?.subscription?.is_active;
          const highlight = !hasActivePlan && i === 1;
          const discounted = couponInfo ? Math.round(p.price * (1 - couponInfo.percent_off / 100)) : null;
          return (
            <div key={p.id} data-testid={`package-${p.id}`}
                 className={`relative flex flex-col border bg-card p-8 transition-transform duration-200 hover:-translate-y-1 ${isCurrent ? "border-emerald-500 shadow-lg shadow-emerald-500/10" : highlight ? "border-primary shadow-lg shadow-primary/10" : "border-border"}`}>
              {isCurrent && <span className="absolute -top-3 left-8 rounded-full bg-emerald-500 px-3 py-1 text-xs font-medium text-white">Current Plan</span>}
              {!isCurrent && highlight && <span className="absolute -top-3 left-8 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">Popular</span>}
              <Icon className="h-8 w-8 text-primary" />
              <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight">{p.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>
              <div className="mt-6 flex items-end gap-2">
                {discounted !== null ? (
                  <>
                    <span className="font-heading text-4xl font-bold tracking-tighter">₹{(discounted / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    <span className="mb-1 text-sm text-muted-foreground line-through">₹{(p.price / 100).toLocaleString("en-IN")}</span>
                    <span className="mb-1 text-sm text-muted-foreground">/{p.validity_days}d</span>
                  </>
                ) : (
                  <>
                    <span className="font-heading text-4xl font-bold tracking-tighter">₹{(p.price / 100).toLocaleString("en-IN")}</span>
                    <span className="mb-1 text-sm text-muted-foreground">/{p.validity_days} days</span>
                  </>
                )}
              </div>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />
                  {p.max_migrations === -1 ? <span className="flex items-center gap-1">Unlimited migrations</span> : `${p.max_migrations} migrations`}</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />Up to {p.storage_mb != null ? (p.storage_mb >= 1024 ? `${(p.storage_mb/1024).toFixed(p.storage_mb % 1024 === 0 ? 0 : 1)}GB` : `${p.storage_mb}MB`) : `${p.storage_per_mailbox_gb}GB`} per mailbox</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{p.max_mailboxes === -1 ? "Unlimited mailboxes" : `${p.max_mailboxes} mailboxes`}</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />Real-time progress & logs</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />Recurring sync & scheduling</li>
              </ul>
              <Button className={`mt-8 w-full gap-2 ${isCurrent ? "bg-emerald-600 hover:bg-emerald-600" : ""}`}
                      disabled={busy === p.id || sub?.is_admin || isCurrent}
                      data-testid={`buy-${p.id}`} onClick={() => (p.price === 0 ? startFree(p) : buy(p))}>
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? <Check className="h-4 w-4" /> : null}
                {sub?.is_admin ? "Admin (free)"
                  : isCurrent ? "Current Plan"
                  : hasActivePlan ? "Upgrade"
                  : p.price === 0 ? "Start Free" : "Buy Plan"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
